import React, { useState, useEffect } from "react";
import { Box, Typography, Alert } from "@mui/material";
import axios from "axios";
import TableWithPagination from "../../../components/TableWithPagination";
import { useLoanContext } from "../LoanContext";
import { useParams } from "react-router-dom";
import { formatDateToDDMMYYYY } from "../../../utils/dateUtils";

interface RateSchedule {
  id: string;
  name: string;
  rateType?: string | null;
  event: string;
  date: string | Date;
  floatingBaseRate?: number | null;
  margin?: number | null;
  interestRate: number | null;
}

const parseDateSafe = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;

  const str = String(v).trim();
  if (!str) return null;

  // First, try native parsing (covers ISO 8601 and timestamp strings)
  const iso = new Date(str);
  if (!isNaN(iso.getTime())) return iso;

  // Fallback: DD/MM/YYYY
  const ddmmyyyy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    const year = y.length === 2 ? Number(`20${y}`) : Number(y);
    const parsed = new Date(year, Number(m) - 1, Number(d));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const RateScheduleTab: React.FC = () => {
  const { loan } = useLoanContext();
  const loanId = useParams().loanId;

  const [rateSchedule, setRateSchedule] = useState<RateSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loanId || !loan) {
      setError(!loanId ? "No loanId provided." : "Loading loan data...");
      setLoading(false);
      return;
    }
    const fetchRateSchedule = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`/api/rate-schedule?loanId=${loanId}`, { withCredentials: true });
        const data = Array.isArray(response.data) ? response.data : [];
        setRateSchedule(data);
      } catch (err: unknown) {
        setError(String((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Failed to fetch rate schedule."));
      } finally {
        setLoading(false);
      }
    };
    fetchRateSchedule();
  }, [loanId, loan]);

  if (loading)
    return (
      <Box textAlign="center" mt={8}>
        <Typography sx={{ color: "#fff" }}>Loading rate schedule...</Typography>
      </Box>
    );
  if (error)
    return (
      <Box textAlign="center" mt={8}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

    const formatPercent = (value: unknown): string => {
      if (value === undefined || value === null || value === "") return "—";
      const numValue = parseFloat(String(value));
      return isNaN(numValue) ? "—" : `${numValue.toFixed(2)}%`;
    };

  return (
    <Box sx={{ width: "100%", maxWidth: "1280px", mx: "auto", px: 2 }}>
      <Box sx={{ width: "100%", overflowX: "auto", borderRadius: 2 }}>
        <TableWithPagination
          columns={[
            {
              label: "Event",
              key: "event",
              renderCell: (row: Record<string, unknown>) =>
                (row.event as string) || "—",
            },
            {
              label: "Rate Type",
              key: "rateType",
              renderCell: (row: Record<string, unknown>) => {
                const rawValue = String(row.rateType ?? "").trim();
                if (!rawValue) return "—";
                return rawValue.toLowerCase() === "floating" ? "Variable" : rawValue;
              },
            },
            {
              label: "Date",
              key: "date",
              sortable: true,
              sortValue: (row: Record<string, unknown>) => {
                const parsed = parseDateSafe(row.date);
                return parsed ? parsed.getTime() : null;
              },
              renderCell: (row: Record<string, unknown>) =>
                row.date
                  ? formatDateToDDMMYYYY(row.date as string)
                  : "—",
            },
            {
              label: "SONIA Base Rate",
              key: "floatingBaseRate",
              renderCell: (row: Record<string, unknown>) =>
                formatPercent(row.floatingBaseRate),
            },
            {
              label: "Margin",
              key: "margin",
              renderCell: (row: Record<string, unknown>) =>
                formatPercent(row.margin),
            },
            {
              label: "Interest Rate (%)",
              key: "interestRate",
              renderCell: (row: Record<string, unknown>) =>
                formatPercent(row.interestRate),
            }
          ]}
          rows={rateSchedule as unknown as Record<string, unknown>[]}
          getRowKey={(row, index) =>
            `${row.id || "unknown"}-${row.name || "na"}-${row.date || "na"}-${index ?? 0}`
          }
          rowsPerPage={10}
        />
      </Box>
    </Box>
  );
};

export default RateScheduleTab;

